import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/core';
import { useDrop, type DragObjectWithType, type DropTargetMonitor } from 'react-dnd';
import { useDispatch, useSelector } from 'react-redux';

import { DROP_TARGET_TYPES, ELEMENT_DND_TYPE } from '../../../../common/elements/elementConstants';
import { isSingleEditorTaskList } from '../../../../common/taskLists/taskListContentVersionUtils';
import { getElementId, getTextContent } from '../../../../common/elements/utils/elementPropertyUtils';
import { asObject } from '../../../../common/utils/immutableHelper';
import type { ImMNElement } from '../../../../common/elements/elementModelTypes';
import type { TiptapContent } from '../../../../common/tiptap/tiptapTypes';
import { moveTaskListItemsToTaskList } from '../../../../common/tiptap/extensions/taskList/taskListItemDropUtils';
import {
    getTaskItemInsertionIndex,
    getTaskListDropIndicator,
    getTaskListDropTarget,
    type TaskListDropIndicator,
} from './taskListDropGeometryUtils';
import { moveElementsToTrash } from '../../actions/elementShortcutActions';
import { getNewTransactionId } from '../../../utils/undoRedo/undoRedoTransactionManager';
import { getCurrentBoardIdFromState } from '../../../reducers/currentBoardId/currentBoardIdSelector';
import type { XYCoord } from 'dnd-core';
import { isString } from 'lodash';

type TaskItemDrag = DragObjectWithType & {
    taskItemDrag?: { sourceElementId: string; sourceTaskId?: string };
    element?: ImMNElement;
    draggedElementIds?: string[];
    hoveredTypesRegistry?: Record<string, string>;
};

type TaskListDropResult = {
    taskListDrop?: { elementId: string; insertionPosition: number; reparentFollowingTaskItemsFromPosition?: number };
    taskListElementDrop?: true;
};

/**
 * Checks whether an element drag represents exactly one source V2 task-list element.
 */
const isTaskListElementDrag = ({ item, targetElementId }: { item: TaskItemDrag; targetElementId: string }): boolean =>
    !item.taskItemDrag &&
    item.draggedElementIds?.length === 1 &&
    !!item.element &&
    getElementId(item.element) !== targetElementId &&
    isSingleEditorTaskList(item.element);

/**
 * Determines whether two indicator positions render at the same location.
 */
const isSameDropIndicator = (
    firstIndicator: TaskListDropIndicator | null,
    secondIndicator: TaskListDropIndicator | null,
): boolean => firstIndicator?.top === secondIndicator?.top && firstIndicator?.left === secondIndicator?.left;

/**
 * Registers this task list as a valid list target so the drag preview
 * will go transparent on hover of another list.
 */
const setTaskListHoverType = (item: TaskItemDrag, monitor: DropTargetMonitor): void => {
    const targetId = monitor.getHandlerId();

    if (!item.hoveredTypesRegistry || !isString(targetId)) return;

    item.hoveredTypesRegistry[targetId] = DROP_TARGET_TYPES.LIST;
};

/**
 * Determines whether we have all the info needed to handle a DnD event and that it's a task item or task list drag.
 */
const shouldHandleDndEvent = (
    editor: Editor | null,
    clientOffset: XYCoord | null,
    item: TaskItemDrag,
    elementId: string,
): editor is Editor => {
    if (!editor) return false;
    if (!clientOffset) return false;

    if (item.taskItemDrag) return true;

    return isTaskListElementDrag({ item, targetElementId: elementId });
};

/**
 * Determines whether the hovered gap is within the dragged task's subtree in this editor.
 */
const isHoveringDraggedTaskSubtree = ({
    rows,
    insertionIndex,
    sourceElementId,
    sourceTaskId,
    targetElementId,
}: {
    rows: HTMLElement[];
    insertionIndex: number;
    sourceElementId: string;
    sourceTaskId?: string;
    targetElementId: string;
}): boolean => {
    if (!sourceTaskId || sourceElementId !== targetElementId) return false;

    const sourceTaskRow = rows.find((row) => row.dataset.taskId === sourceTaskId);

    if (!sourceTaskRow) return false;

    return [rows[insertionIndex - 1], rows[insertionIndex]].some((row) => !!row && sourceTaskRow.contains(row));
};

/**
 * Gets a task item's document position from its rendered row.
 */
const getTaskItemPosition = ({ editor, row }: { editor: Editor; row: HTMLElement | undefined }): number | undefined => {
    const taskItemElement = row?.closest('li');
    const taskListElement = taskItemElement?.parentElement;

    if (!taskItemElement || !taskListElement) return undefined;

    const taskIndex = Array.from(taskListElement.children).indexOf(taskItemElement);

    return taskIndex === -1 ? undefined : editor.view.posAtDOM(taskListElement, taskIndex);
};

/**
 * Connects a V2 task list to task-item drops and exposes the hovered insertion indicator.
 */
const TaskListDropTarget: React.FC<{
    editor: Editor | null;
    elementId: string;
    setParentHoveredChildAcceptsDrop?: (hoveredChildAcceptsDrop: boolean) => void;
    children: (args: {
        connectDropTarget: React.RefCallback<HTMLDivElement>;
        dropIndicator: TaskListDropIndicator | null;
    }) => React.ReactNode;
}> = ({ editor, elementId, setParentHoveredChildAcceptsDrop, children }) => {
    const dispatch = useDispatch();
    const currentBoardId = useSelector(getCurrentBoardIdFromState);
    const [dropIndicator, setDropIndicator] = useState<TaskListDropIndicator | null>(null);
    const dropTargetElementRef = useRef<HTMLDivElement | null>(null);

    const [{ isOver, canDrop }, connectDropTarget] = useDrop<
        TaskItemDrag,
        TaskListDropResult,
        { isOver: boolean; canDrop: boolean }
    >({
        accept: ELEMENT_DND_TYPE,
        canDrop: (item) => {
            if (!editor) return false;

            if (item.taskItemDrag) return true;
            if (!currentBoardId) return false;

            return isTaskListElementDrag({ item, targetElementId: elementId });
        },
        hover: (item, monitor) => {
            const clientOffset = monitor.getClientOffset();

            if (!shouldHandleDndEvent(editor, clientOffset, item, elementId)) {
                setDropIndicator(null);
                return;
            }

            const rows = Array.from(editor.view.dom.querySelectorAll<HTMLElement>('.task-item-row'));
            const updatedInsertionIndex = getTaskItemInsertionIndex({ rows, clientY: clientOffset!.y });
            const updatedDropTarget = getTaskListDropTarget({
                rows,
                insertionIndex: updatedInsertionIndex,
                clientX: clientOffset!.x,
            });

            // Don't show the drop indicator / handle drop, when hovering itself
            const isOverSubtree =
                !!item.taskItemDrag &&
                isHoveringDraggedTaskSubtree({
                    rows,
                    insertionIndex: updatedInsertionIndex,
                    sourceElementId: item.taskItemDrag.sourceElementId,
                    sourceTaskId: item.taskItemDrag.sourceTaskId,
                    targetElementId: elementId,
                });

            if (isOverSubtree) {
                setDropIndicator(null);
                return;
            }

            setTaskListHoverType(item, monitor);

            const updatedDropIndicator =
                updatedDropTarget &&
                getTaskListDropIndicator({
                    rows,
                    insertionIndex: updatedInsertionIndex,
                    dropTargetElement: dropTargetElementRef.current,
                    taskListDropTarget: updatedDropTarget,
                });

            setDropIndicator((currentDropIndicator) =>
                isSameDropIndicator(currentDropIndicator, updatedDropIndicator)
                    ? currentDropIndicator
                    : updatedDropIndicator,
            );
        },
        drop: (item, monitor) => {
            const clientOffset = monitor.getClientOffset();

            if (!shouldHandleDndEvent(editor, clientOffset, item, elementId)) return undefined;

            const rows = Array.from(editor.view.dom.querySelectorAll<HTMLElement>('.task-item-row'));
            const insertionIndex = getTaskItemInsertionIndex({ rows, clientY: clientOffset!.y });
            const dropTarget = getTaskListDropTarget({
                rows,
                insertionIndex,
                clientX: clientOffset!.x,
            });

            const isOverSubtree =
                item.taskItemDrag &&
                isHoveringDraggedTaskSubtree({
                    rows,
                    insertionIndex,
                    sourceElementId: item.taskItemDrag.sourceElementId,
                    sourceTaskId: item.taskItemDrag.sourceTaskId,
                    targetElementId: elementId,
                });

            if (isOverSubtree) return undefined;
            if (!dropTarget) return undefined;

            const insertionPosition = editor.view.posAtDOM(dropTarget.taskListElement, dropTarget.taskIndex);

            if (item.taskItemDrag) {
                return {
                    taskListDrop: {
                        elementId,
                        insertionPosition,
                        reparentFollowingTaskItemsFromPosition: dropTarget.reparentsFollowingTaskItems
                            ? getTaskItemPosition({ editor, row: rows[insertionIndex] })
                            : undefined,
                    },
                };
            }

            // Dragging a full task list from here on
            if (!currentBoardId) return undefined;

            const sourceContent = asObject<TiptapContent>(getTextContent(item.element!));
            const transactionId = getNewTransactionId();
            const didMove = moveTaskListItemsToTaskList({
                sourceContent,
                targetEditor: editor,
                targetPosition: insertionPosition,
                transactionId,
            });

            if (!didMove) return undefined;

            dispatch(
                moveElementsToTrash({
                    elementId: getElementId(item.element!),
                    currentBoardId,
                    transactionId,
                }),
            );

            return { taskListElementDrop: true };
        },
        collect: (monitor) => ({
            isOver: monitor.isOver({ shallow: true }),
            canDrop: monitor.canDrop(),
        }),
    });

    // Prevent an enclosing list or unsorted notes container from rendering its own indicator
    // while this task list is the shallow drop target that will handle the task-item drop.
    useEffect(() => {
        setParentHoveredChildAcceptsDrop?.(isOver && canDrop);
    }, [canDrop, isOver, setParentHoveredChildAcceptsDrop]);

    const connectDropTargetRef = useCallback(
        (element: HTMLDivElement | null) => {
            dropTargetElementRef.current = element;
            connectDropTarget(element);
        },
        [connectDropTarget],
    );

    return <>{children({ connectDropTarget: connectDropTargetRef, dropIndicator: isOver ? dropIndicator : null })}</>;
};

export default TaskListDropTarget;
